import * as $ from 'jquery';

/**
 * Checks whether the given object has the given key.
 *
 * @param {Object} obj - The object to check.
 * @param {*}      key - The key to check for.
 *
 * @return {boolean} Returns `true` if the object has the given key, otherwise `false`.
 */
export const hasKeyInObject = ( obj, key ) => ( key in obj );

/**
 * Checks whether the given object is empty.
 *
 * @param {Object} obj - The object to check.
 * 
 * @return {boolean} Returns `true` if the object has no enumerable properties, otherwise `false`.
 */
export const isEmptyObject = ( obj ) => ( Object.keys( obj ).length === 0 );

/**
 * Return a copy of the object only containing the properties which pass the
 * given predicate.
 *
 * @param {Object} obj - The object to filter.
 * @param {Function} predicate - A function which takes a value and returns a
 *                               boolean. If the function returns `true`, the
 *                               property is skipped.
 *
 * @return {Object} A new object containing the filtered properties.
 */
export function objectFilter( obj, predicate ) {
    let result = {}, key;

    for ( key in obj ) {
        if ( obj.hasOwnProperty( key ) && ! predicate( obj[key] ) ) {
            result[key] = obj[key];
        }
    }

    return result;
}

/**
 * Groups an array of objects by the value of the given key.
 *
 * @param {Object[]} xs - The array of objects to group.
 * @param {string} key - The key to group by.
 * @returns {Object} An object with property names derived from the values of the given key and values of arrays of objects with the same key values.
 */
export function groupBy( xs, key: string ){
    return xs.reduce( ( rv, x ) => {

        ( rv[x[key]] = rv[x[key]] || [] ).push( x );

        return rv;
    }, {} )
}

/**
 * Checks if a specified key exists in an array.
 *
 * @param {Array<any>} args - The array to search within.
 * @param {any} key - The key to look for in the array.
 * @returns {boolean} True if the key is found in the array, otherwise false.
 */

export function hasKeyInArray( args, key ) {
    return -1 !== args.indexOf( key );
}

/**
 * Generate a key from a given text string, by:
 * 1. Trimming whitespace from the start and end of the string
 * 2. Converting to lowercase
 * 3. Replacing multiple whitespace characters with a single underscore
 * @param {string} text The text string to generate a key from
 * 
 * @returns {string} The generated key
 */
export const generateKey = ( text: string ) => {
    return text.toString().trim().toLowerCase().replace( /\s+/g, '_' );
}

/**
 * Removes special characters from a given string.
 *
 * This function removes all non-alphanumeric characters, leaving only letters and numbers.
 * It will also remove spaces by default.
 *
 * @param {string} str The input string from which special characters will be removed.
 * @returns {string} A string with special characters removed.
 */
export const removeSpecialCharacters = str => {
    return str.replace( /[^a-zA-Z0-9]/g, '' );
}

/**
 * Capitalize the first letter of a given word.
 *
 * @param {string} word The word to be capitalized.
 * @returns {string} The capitalized word.
 */
export const capitalize = word => {
    return word[0].toUpperCase() + word.substring(1).toLowerCase();
}

/**
 * Encodes a string as a Base64 string with Unicode support.
 * @param {string} str - The string to encode.
 * @returns {string} - The Base64 encoded string.
 */
export const b64EncodeUnicode = str => {
    return btoa( encodeURIComponent( str ).replace( /%([0-9A-F]{2})/g, function( match, p1 ) {
        return String.fromCharCode( Number( ( '0x' + p1 ) ) );
    } ) );
}

/**
 * Checks if an item exists in an array, and if not, adds the item to the array.
 * If the given array is not an array, creates a new array with the item.
 * 
 * @param {Array<any>} array The array to check and push the item to.
 * @param {any} item The item to check for and add to the array.
 * @returns {Array<any>} The array with the item added, if it did not exist.
 */
export const checkAndPushKeyToArray = ( array, item ) => {
    if( Array.isArray( array ) ) {
        if( -1 === array.indexOf( item ) ) {
            array.push( item );
        }
    } else {
        array = [ item ];
    }
    
    return array;
}

/**
 * Get the default lock position for a given component, based on the component's type and name.
 * 
 * @param {string} type The type of the component. Only 'door' is currently supported.
 * @param {string} newComponentName The name of the component.
 * 
 * @returns {Object} An object containing the default lock position, whether the lock is custom, whether the hinge is enabled, and the price of the lock. If the component type is not 'door', or the default lock position is not 'left' or 'right', the function returns undefined.
 */
export const getDefaultLock = ( type, newComponentName ) => {
    if( 'door' !== type ) {
        return undefined;
    }

    const defaultLock = $( `[data-upgrade-component-name="${newComponentName}"]` ).attr( 'data-lock-position' );
    if( 'left' === defaultLock || 'right' === defaultLock ) {
        const enableHinge   = '1' == $( `[data-upgrade-component-name="${newComponentName}"]` ).attr( 'data-enable-hinge' );
        const lockPrice     = $( `[data-upgrade-component-name="${newComponentName}"]` ).attr( 'data-lock-price' );

        return { defaultLock, customLock: defaultLock, enableHinge, lockPrice };
    }

    return undefined;
}

/**
 * Get the default color for a given component, based on the component's name.
 *
 * @param {string} componentName The name of the component.
 *
 * @returns {Object} An object containing the key, name, price, components, price type, texture type, texture image, and texture color of the default color for the given component. If no default color is found, an empty object is returned.
 */
export const getDefaultComponentColor = ( componentName ) => {
    let result = {};
    $( '.wrap-component-color ul li' ).each( function( i, e ) {
        let components = $( e ).data( 'default-components' );
        if( components ) {
            components = JSON.parse( window.atob( components ) );
            if( hasKeyInArray( components, componentName ) ) {
                const key = $( e ).data( 'key' );
                const name = $( e ).data( 'name' );
                const price = $( e ).data( 'price' );
                let components = $( e ).data( 'components' );
                components = components ? JSON.parse( window.atob( components ) ) : [];
                const priceType = $( e ).data( 'price-type' );
                const textureType = $( e ).data( 'texture-type' );
                const textureImage = $( e ).data( 'texture-image' );
                const textureColor = $( e ).data( 'texture-color' );

                result = {
                    key,
                    name,
                    price,
                    components,
                    priceType,
                    textureType,
                    textureImage,
                    textureColor
                };

                return;
            }
        }
    } );

    return result;
}

/**
 * Finds the index of the first object in an array where the specified key has a specific value.
 *
 * @param {Array<Object>} object - The array of objects to search through.
 * @param {string} key - The key to check in each object.
 * @param {*} value - The value to match against the specified key in each object.
 * @returns {number} The index of the first matching object, or -1 if no match is found.
 */

export const findIndexObject = ( object, key, value ) => {
    for( let i = 0; i < object.length; i++ ) {
        if( object[i][`${key}`] === value ) {
            return i;
        }
    }

    return -1;
}

/**
 * Checks if the enable_line_item_cost option is enabled.
 * @returns {boolean} True if enable_line_item_cost is enabled, false otherwise.
 */
export const isEnableLineItemCost = () => {
    return (<any>window).ssb.enable_line_item_cost == '1';
}

/**
 * Converts a given text string into a URL-friendly slug.
 *
 * @param {string} text - The input text to convert to a slug.
 * @returns {string} The converted slug.
 */

export const convertToSlug = ( text : string ) => {
    return text.toLowerCase().replace( / /g, "-" ).replace( /[^\w-]+/g, "" );
}

/**
 * Filters an array of objects based on a specified key and value.
 * 
 * @param {Array<Object>} array - The array of objects to filter.
 * @param {string} key - The key to check in each object.
 * @param {string} value - The value to search for within the specified key's value.
 * @returns {Array<Object>} A new array containing only the objects that contain the specified value in the specified key.
 */
export const filterMeshByName = ( array, key, value ) => {
    return array.filter( obj => obj[key].includes( value ) );
}

/**
 * Checks if the "Runner Direction" feature is enabled.
 *
 * @returns {boolean} True if the feature is enabled, false otherwise.
 */
export const isEnableRunnerDirection = () => ( ( <any>window ).ssb.enable_runner_direction == '1' );

/**
 * Determines if the "Allow Swap Side" option is available.
 * 
 * @returns {boolean} True if the "Allow Swap Side" option is available, false otherwise.
 */

export const isAllowSwapSide = () => {
    return $( '[name="ssb-group-upgrade-side-gable"]' ).length > 0;
}

/**
 * Checks if the manufacture feature is enabled.
 *
 * @returns {boolean} True if the manufacture feature is enabled, otherwise false.
 */
export const isEnableManufactureFeature = () => {
    return (<any>window).ssb.is_manufacturer_feature == '1';
}

/**
 * Checks if the "Swap Side" option is enabled.
 * 
 * @returns {boolean} True if the "Swap Side" option is enabled, false otherwise.
 */
export const isSwapSide = () => {
    return 'enable' === $( '[name="ssb-group-upgrade-side-gable"]:checked' ).val();
}

/**
 * Gets the label for a given side.
 *
 * @param {string} side - The side (e.g. 'left', 'right', 'front', 'back') to get the label for.
 * @param {string} defaultLabel - The default label to use if no special label is defined.
 * @returns {string} The label for the side, either the default label if no special label is defined or the special label if it is.
 */
export const getSideLabel = ( side, defaultLabel ) => {
    const dataSideLabel = JSON.parse( atob( $( '#js-data-side-label' ).data( 'side-label' ) ) );

    if( dataSideLabel[side] ) {
        return dataSideLabel[side];
    }

    return defaultLabel;
}

/**
 * Retrieves the label for a given upgrade component name.
 * 
 * @param {string} name - The upgrade component name.
 * @returns {string} The label for the given upgrade component name.
 */
export const getLabel = name => {
    return $( `[data-upgrade-component-name="${name}"]` ).data( 'option-value' );
}

/**
 * Check if all keys exist in the bones object.
 *
 * @param {string[]} keys   List of keys to check.
 * @param {Object}   bones  Object to check against.
 *
 * @return {boolean} True if all keys exist, false if any one of them does not.
 */
export const isExistBones = ( keys, bones ) => {
    for( let i = 0; i < keys.length; i++ ) {
        if( ! bones[keys[i]] ) {
            return false;
        }
    }

    return true;
}

/**
 * Filters an array of objects to include only unique items based on the 'name' property.
 * If an item has a priority field, it will be prioritized over items without the priority field.
 *
 * @param {Array<Object>} arr - The array of objects to filter.
 * @param {string} priorityField - The field to prioritize when filtering unique items.
 * @returns {Array<Object>} A new array containing only unique items.
 */
export const filterUniqueItems = ( arr, priorityField, priorityValue ) => {
    const results = [];
    
    for( let i = 0; i < arr.length; i++ ) {
        const wall = arr[i];
        const index = results.findIndex( result => result.name === wall.name );
        const priorityData = wall.hasOwnProperty( priorityField ) ? wall[priorityField] : [];

        if( index === -1 && ( hasKeyInArray( priorityData, priorityValue ) || 0 === priorityData.length ) ) {
            results.push( wall );
        } else {
            if( hasKeyInArray( priorityData, priorityValue ) ) {
                results[index] = wall;
            }
        }
    }

    return results;
}

/**
 * Get component Label from Dom
 * 
 * @param {String} name name of component
 * 
 * @returns {String} label of component
 */
export const getComponentLabel = ( name ) => {
    const customName = jQuery( `[data-upgrade-component-name='${name}']` ).val();
    if( customName ) {
        // If component has custom name, then return it
        return customName;
    }

    // If component doesn't have custom name, then return label from name
    return name.split( '_' ).map( n => n.charAt( 0 ).toUpperCase() + n.slice( 1 ) ).join( ' ' );
}

/**
 * Converts a date string to a Unix timestamp in seconds.
 *
 * @param {string} dateString Date string to convert.
 * @returns {number} Unix timestamp in seconds.
 */
export const convertDateTime = ( dateString ) => {
    return Math.floor( new Date( dateString ).getTime() / 1000 );
}

/**
 * Get version of component from component data
 * 
 * @param {String} componentName name of component
 * @param {Object} componentData component data
 * 
 * @returns {String} version of component
 */
export const getVersionComponent = ( componentName, componentData ) => {
    const componentNetWorkData = componentData[componentName];
    const { updated_at: componentUpdateAt } = componentNetWorkData;
    return componentUpdateAt ? `?ver=${ encodeURIComponent( convertDateTime( componentUpdateAt ) ) }` : '';
}

/**
 * Returns the inner right width for the deluxe porch.
 *
 * This method reads the side wall dimension from the `shedpro` object
 * and returns it as a number. If the value is null or not a number,
 * it returns 4. If the value is 0 or less, it returns 0.001.
 *
 * @returns {number} The inner right width of the deluxe porch.
 */
export const deluxePorchGetInnerRightWidth = () => {
    const w = ( <any> window );
    const data = w.shedpro;
    const sideWallDimension = data['side_wall_dimension'];
    if( null === sideWallDimension ) {
        return 4;
    }

    const dimension = Number( sideWallDimension );
    return dimension <= 0 ? 0 : dimension;
}

/**
 * Removes duplicate objects from an array of objects.
 *
 * @param {Array<Object>} arr The array of objects to remove duplicates from.
 * @returns {Array<Object>} A new array with no duplicate objects.
 */
export const removeDuplicates = arr => {
    const seen = new Set();
    return arr.filter( point => {
        const key = JSON.stringify( point ); // Works for nested objects too
        return seen.has( key ) ? false : seen.add( key );
    } );
}

/**
 * Formats a color name by appending its hex code if it is a color picker.
 * 
 * @param {string} name - The name of the color.
 * @param {string} type - The type of the color, either 'color_picker' or a material name.
 * @param {string} code - The hex code of the color.
 * 
 * @returns {string} The formatted color name.
 */
export const formatColorName = ( name, type, code ) => {
    return 'color_picker' === type ? `${name} - ${code}` : name;
}
